import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import { Link } from 'react-router-dom';
import { SEOHead, organizationSchema, mobileAppSchema, BRAND } from './SEOUtils';

export default function BlackPolyamoryDatingAppPage() {
  const schemas = [organizationSchema, mobileAppSchema];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Black Polyamory Dating App | Find Poly Partners | UnityLove"
        description="UnityLove is where Black poly individuals connect—whether polyamorous, polygamous, or practicing intentional monogamy. Built on consent and community."
        canonical="/black-polyamory-dating-app"
        schemas={schemas}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            The Black Polyamory Dating App Built for Real Connection
          </h1>
          <p className="text-xl text-slate-300 mb-4 max-w-2xl mx-auto">
            UnityLove is where Black poly individuals and couples connect—whether you practice polyamory, are building a polygamous family structure, or practice intentional monogamy within a community that respects Black love and values.
          </p>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Black poly-centered. Polygamy welcomed. Monogamy respected. All rooted in honesty and consent.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Start Dating Free
            </Link>
            <a
              href={BRAND.playStoreUrl}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Get the App
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            What Is Black Polyamory and Why Does It Matter?
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Polyamory is the practice of engaging in multiple romantic relationships simultaneously, with the knowledge and consent of everyone involved. Black polyamory specifically centers the experiences, values, and cultural contexts of Black individuals who choose this relationship style.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            For many Black people, polyamory offers a path to relationship authenticity that monogamy-centered dating apps simply can't provide. It's about building chosen family, distributing emotional labor, and creating love networks that support rather than constrain.
          </p>
          <p className="text-lg text-slate-700">
            UnityLove's Black polyamory dating app provides the infrastructure for these connections—matching you with people who share your values, understand your lifestyle, and are ready to build something meaningful together.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Features Designed for Black Polyamorous Dating
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Polycule-Friendly Profiles</h3>
              <p className="text-slate-700">Display your existing relationships, link partner profiles, and let potential matches understand your current constellation. No hiding, no explaining—just transparency.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Relationship Style Matching</h3>
              <p className="text-slate-700">Whether you practice hierarchical polyamory, relationship anarchy, or something in between, our algorithm connects you with compatible partners.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Community Events</h3>
              <p className="text-slate-700">Virtual speed dating, discussion groups, and community meetups for Black poly individuals. Build friendships alongside romantic connections.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Privacy Controls</h3>
              <p className="text-slate-700">Control who sees your profile, manage visibility settings, and date safely. Your polyamorous journey is yours to share—or not—on your terms.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            The Difference Between Polyamory and Polygamy
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            While both involve multiple partners, polyamory and polygamy are distinct. Polyamory emphasizes multiple loving relationships based on equality and mutual consent. Polygamy traditionally refers to marriage with multiple spouses, often with specific cultural or religious structures.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            UnityLove welcomes members exploring both paths. Whether you're seeking <Link to="/black-polygamy-dating" className="text-rose-600 hover:text-rose-700 underline">Black polygamy dating</Link> or polyamorous connections, our platform supports diverse relationship structures within the Black community.
          </p>
          <p className="text-lg text-slate-700">
            What matters most is that your relationships are built on honesty, communication, and mutual respect—values that UnityLove cultivates across all relationship styles.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            A Community That Reflects You
          </h2>
          <div className="bg-slate-50 p-8 rounded-xl">
            <p className="text-lg text-slate-700">
              UnityLove is built for Black singles, couples, and poly families who want a dating
              space that honors their full identity. Bring your culture, your boundaries, and your
              vision for partnership — and find people who respect all of it.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Your Polyamorous Journey Starts Here</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Become a founding member of the Black poly dating community on UnityLove. Create your free profile today and start connecting.
          </p>
          <Link
            to="/auth?mode=signup"
            className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
          >
            Join UnityLove Free
          </Link>
        </section>

        {/* Choose Your Structure Section */}
        <section className="mb-12 bg-rose-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Choose the Relationship Structure That Fits Your Values
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polyamory</h3>
              <p className="text-slate-600 text-sm">Multiple loving relationships practiced with honesty, consent, and open communication.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polygamy</h3>
              <p className="text-slate-600 text-sm">Structured multi-partner or multi-spouse commitment, often rooted in family, culture, and long-term responsibility.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Intentional Monogamy</h3>
              <p className="text-slate-600 text-sm">Traditional one-partner commitment built on clarity, trust, and shared purpose.</p>
            </div>
          </div>
          <p className="text-center text-slate-700 mt-6">
            Whatever your structure, UnityLove prioritizes honesty, consent, and respect.
          </p>
        </section>

        {/* Related Pages */}
        <section className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Related Topics</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black Poly Dating</Link>
            <Link to="/ethical-non-monogamy-dating" className="text-rose-600 hover:text-rose-700 underline">Ethical Non-Monogamy</Link>
            <Link to="/poly-dating-app" className="text-rose-600 hover:text-rose-700 underline">Poly Dating App</Link>
            <Link to="/about-unitylove" className="text-rose-600 hover:text-rose-700 underline">About Us</Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-600">
          <p>© {new Date().getFullYear()} UnityLove. Black polyamory dating for conscious connection.</p>
        </div>
      </footer>
    </div>
  );
}
